import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackUserEvent, isImpersonatingSession } from "@/utils/userTracking";
import { apiClient } from "@rti/shared";

const isProduction = window.location.hostname === "app.resinsmart.ai";
const hubspotTrackingEnabled =
  isProduction && ((import.meta as any).env?.VITE_HUBSPOT_TRACKING_ENABLED as string | undefined) === "true";

declare global {
  interface Window {
    _hsq?: any[];
  }
}

export const useHubspotTracking = (
  user?: { email?: string; first_name?: string; last_name?: string },
  metadata?: Record<string, any>
) => {
  const location = useLocation();
  const lastTrackedPathRef = useRef<string | null>(null);

  const noop = () => {};
  const hubspotFns = {
    trackEvent: noop,
    trackReportView: noop,
    trackDashboardInteraction: noop,
    trackUserEvent: noop,
  };

  useEffect(() => {
    if (!hubspotTrackingEnabled || isImpersonatingSession()) return;
    if (user?.email) {
      window._hsq = window._hsq || [];
      window._hsq.push([
        "identify",
        {
          email: user.email,
          firstname: user.first_name || "",
          lastname: user.last_name || "",
        },
      ]);
    }
  }, [user]);

  useEffect(() => {
    const path = location.pathname;
    if (isImpersonatingSession()) {
      return;
    }
    if (path === "/authorization" || path.includes("/authorization") || path.includes("/user-activity") || path === "/free-trial") {
      return;
    }
    if (path.startsWith("/marketIntel")) {
      return;
    }
    if (lastTrackedPathRef.current === path) {
      return;
    }
    lastTrackedPathRef.current = path;

    const pathParts = path.split('/').filter(Boolean);
    let title = "ResinSmart";
    if (pathParts.length > 0) {
      const formattedParts = pathParts.map(part =>
        part
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
      title = formattedParts.join(' ') + " | ResinSmart";
    }
    document.title = title;

    if (hubspotTrackingEnabled && window._hsq) {
      window._hsq.push(["setPath", path]);
      window._hsq.push(["trackPageView"]);
    }
    try {
      const clarity = (window as any).clarity;
      if (typeof clarity === 'function') {
        clarity('event', 'page_view');
        clarity('set', 'page_path', path);
      }
    } catch {}

    try {
      const user_id = (window as any).currentUser?.user_id ?? 0;
      if (!user_id) return;
      const url = path;
      const pageTitle = document.title;
      const timestamp = Date.now();
      const baseMetadata = metadata ?? {};
      apiClient.postUserActivity({
        user_id,
        event: "page_view",
        url,
        title: pageTitle,
        timestamp,
        metadata: baseMetadata,
      });

    } catch (err) {
      console.warn("Failed to log page_view activity", err);
    }
  }, [location.pathname]);

  if (!hubspotTrackingEnabled) {
    return hubspotFns;
  }

  const trackEvent = (
    id: string,
    value?: string | number,
    properties: Record<string, any> = {}
  ) => {
    trackUserEvent(id, value, properties);
  };

  const trackReportView = (reportName: string) => trackUserEvent("report_view", reportName);
  const trackDashboardInteraction = (panel: string) => trackUserEvent("dashboard_interaction", panel);

  return { trackEvent, trackReportView, trackDashboardInteraction, trackUserEvent };
};